import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Cloud, Sun, CloudRain, Wind, Droplets, Thermometer, AlertTriangle } from 'lucide-react';
import { TrustBadge } from '@/components/TrustBadge';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const HERO_IMG = 'https://images.unsplash.com/photo-1754810940905-19a8d26f870e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400';

function WeatherIntelligence({ user }) {
  const [weather, setWeather] = useState(null);
  const [advice, setAdvice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const [weatherRes, adviceRes] = await Promise.all([
          axios.get(`${API}/weather`, { withCredentials: true }),
          axios.get(`${API}/weather/advice`, { withCredentials: true })
        ]);
        
        if (weatherRes.data.error) {
          setError(weatherRes.data.message);
        } else {
          setWeather(weatherRes.data);
        }
        setAdvice(adviceRes.data);
      } catch (error) {
        console.error('Error fetching weather:', error);
        setError('Could not load weather data. Please check your location settings.');
      } finally {
        setLoading(false);
      }
    };
    fetchWeather();
  }, []);

  const getWeatherIcon = (code) => {
    if (code === 0 || code === 1) return Sun;
    if (code >= 51 && code <= 67) return CloudRain;
    if (code >= 80) return CloudRain;
    return Cloud;
  };

  const getDayName = (dateStr, idx) => {
    if (idx === 0) return 'Today';
    if (idx === 1) return 'Tomorrow';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-NZ', { weekday: 'short' });
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-96">
          <div className="w-16 h-16 border-4 border-[#8EAA97] border-t-[#1A4331] rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout user={user}>
        <div className="bg-white rounded-3xl p-12 border border-[#1A4331]/5 shadow-sm text-center max-w-2xl mx-auto">
          <Cloud className="w-16 h-16 text-[#8EAA97] mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-[#2D241E] mb-2">Location Required</h3>
          <p className="text-[#5C665C] mb-6">{error}</p>
          <a href="/settings" className="inline-block bg-[#1A4331] hover:bg-[#255C44] text-white font-semibold py-3 px-8 rounded-full transition-colors">Set Location</a>
        </div>
      </Layout>
    );
  }

  const current = weather?.current || {};
  const daily = weather?.daily || {};

  return (
    <Layout user={user}>
      <div className="space-y-6">
        {/* Photo HUD hero */}
        <div className="relative overflow-hidden rounded-[24px]" style={{ boxShadow: '0 10px 30px -6px rgba(26,67,49,0.3)' }} data-testid="current-weather">
          <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(150deg, rgba(15,45,31,0.82), rgba(15,45,31,0.55) 55%, rgba(26,67,49,0.92))' }} />
          <div className="relative p-6 sm:p-8">
            <p className="text-[11px] font-bold tracking-[0.25em] uppercase" style={{ color: '#B4D330' }}>Weather Intelligence</p>
            <p className="text-sm mt-1" style={{ color: 'rgba(250,246,236,0.82)' }}>{weather?.location || user?.location} · smart weather, garden decisions</p>
            <div className="flex items-end justify-between gap-6 mt-5 flex-wrap">
              <div>
                <p className="text-[64px] leading-none font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: '#FAF6EC' }}>{Math.round(current.temperature_2m || 0)}°</p>
                <div className="flex items-center gap-4 mt-3" style={{ color: 'rgba(250,246,236,0.9)' }}>
                  <div className="flex items-center gap-1.5"><Droplets className="w-4 h-4" style={{ color: '#9FC3F2' }} /><span className="text-sm">{current.relative_humidity_2m}%</span></div>
                  <div className="flex items-center gap-1.5"><Wind className="w-4 h-4" style={{ color: '#CFE0F2' }} /><span className="text-sm">{Math.round(current.wind_speed_10m || 0)} km/h</span></div>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="rounded-2xl px-5 py-3 text-center" style={{ background: 'rgba(250,246,236,0.14)', backdropFilter: 'blur(8px)' }}>
                  <Thermometer className="w-4 h-4 mx-auto mb-1" style={{ color: '#F0A58C' }} />
                  <p className="text-[11px]" style={{ color: 'rgba(250,246,236,0.8)' }}>High</p>
                  <p className="text-2xl font-bold" style={{ color: '#FAF6EC', fontFamily: 'Outfit, sans-serif' }}>{Math.round(daily.temperature_2m_max?.[0] || 0)}°</p>
                </div>
                <div className="rounded-2xl px-5 py-3 text-center" style={{ background: 'rgba(250,246,236,0.14)', backdropFilter: 'blur(8px)' }}>
                  <Thermometer className="w-4 h-4 mx-auto mb-1" style={{ color: '#9FC3F2' }} />
                  <p className="text-[11px]" style={{ color: 'rgba(250,246,236,0.8)' }}>Low</p>
                  <p className="text-2xl font-bold" style={{ color: '#FAF6EC', fontFamily: 'Outfit, sans-serif' }}>{Math.round(daily.temperature_2m_min?.[0] || 0)}°</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {weather?.risks && weather.risks.length > 0 && (
          <div className="space-y-3" data-testid="risk-alerts">
            {weather.risks.map((risk, idx) => (
              <div key={idx} className={`p-4 rounded-2xl border ${risk.severity === 'high' ? 'bg-[#E2725B]/10 border-[#E2725B]/30' : risk.severity === 'medium' ? 'bg-[#D4AF37]/10 border-[#D4AF37]/30' : 'bg-[#8AB4F8]/10 border-[#8AB4F8]/30'}`}>
                <div className="flex items-start gap-3">
                  <AlertTriangle className={`w-5 h-5 mt-0.5 ${risk.severity === 'high' ? 'text-[#E2725B]' : risk.severity === 'medium' ? 'text-[#D4AF37]' : 'text-[#8AB4F8]'}`} />
                  <div>
                    <p className="font-semibold text-[#2D241E] capitalize">{risk.type} Alert</p>
                    <p className="text-sm text-[#5C665C] mt-1">{risk.message}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {advice?.advice && (
          <div className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm" data-testid="weather-advice">
            <h2 className="text-2xl font-semibold text-[#1A4331] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Today&apos;s Garden Advice</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-[#8EAA97]/10 rounded-2xl">
                <div className="flex items-center gap-2 mb-2"><Droplets className="w-5 h-5 text-[#8AB4F8]" /><h3 className="font-semibold text-[#2D241E]">Watering</h3></div>
                <p className="text-sm text-[#5C665C] leading-relaxed mb-2">{advice.advice.watering}</p>
                <TrustBadge confidence={advice.trust?.watering?.confidence} why={advice.trust?.watering?.why} basedOn={advice.trust?.watering?.based_on} testId="trust-weather-watering" />
              </div>
              <div className="p-4 bg-[#B4D330]/10 rounded-2xl">
                <div className="flex items-center gap-2 mb-2"><Sun className="w-5 h-5 text-[#D4AF37]" /><h3 className="font-semibold text-[#2D241E]">Transplanting</h3></div>
                <p className="text-sm text-[#5C665C] leading-relaxed mb-2">{advice.advice.transplanting}</p>
                <TrustBadge confidence={advice.trust?.transplanting?.confidence} why={advice.trust?.transplanting?.why} basedOn={advice.trust?.transplanting?.based_on} testId="trust-weather-transplanting" />
              </div>
              <div className="p-4 bg-[#E2725B]/10 rounded-2xl">
                <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-5 h-5 text-[#E2725B]" /><h3 className="font-semibold text-[#2D241E]">Protection</h3></div>
                <p className="text-sm text-[#5C665C] leading-relaxed mb-2">{advice.advice.protection}</p>
                <TrustBadge confidence={advice.trust?.protection?.confidence} why={advice.trust?.protection?.why} basedOn={advice.trust?.protection?.based_on} testId="trust-weather-protection" />
              </div>
              <div className="p-4 bg-[#8AB4F8]/10 rounded-2xl">
                <div className="flex items-center gap-2 mb-2"><Cloud className="w-5 h-5 text-[#8AB4F8]" /><h3 className="font-semibold text-[#2D241E]">Today&apos;s Snapshot</h3></div>
                <p className="text-sm text-[#5C665C] leading-relaxed mb-2">{advice.advice.general}</p>
                <TrustBadge confidence={advice.trust?.general?.confidence} why={advice.trust?.general?.why} basedOn={advice.trust?.general?.based_on} testId="trust-weather-general" />
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm" data-testid="forecast-section">
          <h2 className="text-2xl font-semibold text-[#1A4331] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>7-Day Forecast</h2>
          <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
            {(daily.temperature_2m_max || []).slice(0, 7).map((max, idx) => {
              const code = daily.weather_code?.[idx] || 0;
              const Icon = getWeatherIcon(code);
              return (
                <div key={idx} className="text-center p-4 bg-[#F0F2EB] rounded-2xl">
                  <p className="text-sm font-semibold text-[#2D241E] mb-2">{getDayName(daily.time?.[idx], idx)}</p>
                  <Icon className="w-8 h-8 text-[#5C665C] mx-auto mb-2" />
                  <p className="text-lg font-bold text-[#1A4331]">{Math.round(max)}°</p>
                  <p className="text-xs text-[#5C665C]">{Math.round(daily.temperature_2m_min?.[idx] || 0)}°</p>
                  {daily.precipitation_sum?.[idx] > 0 && (
                    <p className="text-xs text-[#8AB4F8] mt-1">{daily.precipitation_sum[idx].toFixed(1)}mm</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default WeatherIntelligence;
